/* Companion to settings.hide_sold_out_products.
   Sold-out products are dropped in Liquid, but paginate still counts them, so a
   page of 24 can render a single card. This script:
   - fixes the "X Results" counter, the load-more "showing X of Y" and its bar
   - removes load-more once every available product is on screen
   - pulls the next pages until the grid holds a full page worth of products

   The extra pages are fetched straight from the Section Rendering API and their
   cards appended to the grid. It deliberately does not click the load-more
   button: that component replaces the whole section and restores the scroll
   position it captured at click time, which yanked the reader back up whenever
   they scrolled while a chained page was in flight.

   Real totals come from the ?view=availability views
   (templates/collection.availability.liquid and search.availability.liquid). */
(function () {
  'use strict';

  var initialGrid = document.querySelector('.collection-grid[data-hide-sold-out]');
  if (!initialGrid) return;

  var section = initialGrid.closest('.shopify-section');
  var root = section || document.body;
  var sectionId = section && /^shopify-section-/.test(section.id)
    ? section.id.replace(/^shopify-section-/, '')
    : null;

  var PAGE_SIZE = parseInt(initialGrid.getAttribute('data-products-per-page'), 10) || 24;
  var BATCH = 3; // pages requested in parallel per round
  var STEP_LIMIT = 30; // safety cap on pages pulled per user action
  var MAX_PAGES = 8; // 8 * 250 products; beyond that the counters are left alone
  var cache = {}; // querystring (filters/sort) -> Promise<total|null>

  var lastKey = null; // filter/sort combo the state below belongs to
  var lastTotal = null; // available products for lastKey, null if unknown
  var target = 0; // how many cards the grid should end up holding
  var steps = 0; // pages pulled since the last user action
  var filling = false; // a chained round is in flight
  var exhausted = false; // the last page has been reached
  var userLink = null; // load-more link the user clicked, while it is loading
  var yield_ = null; // in-stock products seen per raw page, to size the next round

  function grid() {
    return root.querySelector('.collection-grid[data-hide-sold-out]');
  }

  function cardCount() {
    return root.querySelectorAll('.products-collection-grid .product-card').length;
  }

  function loadMore() {
    return root.querySelector('pagination-load-more');
  }

  function loadMoreLink() {
    return root.querySelector('pagination-load-more a');
  }

  /* The page the load-more button would load next */
  function nextPage() {
    var link = loadMoreLink();
    if (!link) return null;
    var page = new URL(link.href, location.origin).searchParams.get('page');
    return page ? parseInt(page, 10) : null;
  }

  function sectionUrl(page) {
    var params = new URLSearchParams(location.search);
    params.set('page', page);
    params.set('section_id', sectionId);
    return location.pathname + '?' + params.toString();
  }

  function countUrl(page) {
    var params = new URLSearchParams(location.search);
    params.set('view', 'availability');
    params.set('page', page);
    return location.pathname + '?' + params.toString();
  }

  function cacheKey() {
    var params = new URLSearchParams(location.search);
    params.delete('page');
    params.delete('view');
    params.sort();
    return params.toString();
  }

  function fetchCount(page) {
    return fetch(countUrl(page)).then(function (r) {
      if (!r.ok) throw new Error('availability view ' + r.status);
      return r.text();
    }).then(function (text) {
      return JSON.parse(text.trim());
    });
  }

  function availableTotal() {
    var key = cacheKey();
    if (!cache[key]) {
      cache[key] = fetchCount(1).then(function (first) {
        var pages = first.pages || 1;
        if (pages > MAX_PAGES) return null;
        var rest = [];
        for (var p = 2; p <= pages; p++) rest.push(fetchCount(p));
        return Promise.all(rest).then(function (others) {
          return others.reduce(function (sum, r) {
            return sum + (r.visible || 0);
          }, first.visible || 0);
        });
      }).catch(function () { return null; });
    }
    return cache[key];
  }

  function fetchSection(page) {
    return fetch(sectionUrl(page)).then(function (r) {
      if (!r.ok) throw new Error('section page ' + r.status);
      return r.text();
    }).then(function (html) {
      return new DOMParser().parseFromString(html, 'text/html');
    });
  }

  /* Appends one fetched page. Returns false when it was the last one. */
  function appendPage(doc) {
    var list = root.querySelector('.products-collection-grid');
    if (!list) return false;

    var items = doc.querySelectorAll('.products-collection-grid > li');
    var fragment = document.createDocumentFragment();
    for (var i = 0; i < items.length; i++) {
      fragment.appendChild(document.importNode(items[i], true));
    }
    list.appendChild(fragment);

    // Point the button at the page after the one just appended
    var nextLink = doc.querySelector('pagination-load-more a');
    var current = loadMoreLink();
    if (nextLink && current) {
      current.setAttribute('href', nextLink.getAttribute('href'));
      return true;
    }
    var element = loadMore();
    if (element) element.remove();
    return false;
  }

  /* How many pages to ask for at once: enough to cover what is missing at the
     rate of in-stock products seen so far, so a nearly full grid costs one
     request and a heavily sold-out one gets its pages in parallel. */
  function roundSize() {
    var perPage = yield_ == null ? cardCount() : yield_;
    if (perPage < 1) perPage = 1;
    var pages = Math.ceil((target - cardCount()) / perPage);
    return Math.max(1, Math.min(BATCH, STEP_LIMIT - steps, pages));
  }

  /* Pulls pages while the grid holds fewer cards than `target`. Each page only
     renders its in-stock products, so several can be needed to fill one screen.
     Appending keeps the scroll position: nothing above the viewport moves. */
  function fill() {
    if (filling || exhausted || userLink || !sectionId) return;
    if (steps >= STEP_LIMIT || cardCount() >= target) return;

    var page = nextPage();
    if (page == null) return;

    var rounds = roundSize();
    var requests = [];
    for (var i = 0; i < rounds; i++) requests.push(fetchSection(page + i));
    steps += rounds;
    filling = true;

    var before = cardCount();
    Promise.all(requests).then(function (docs) {
      var pulled = 0;
      for (var i = 0; i < docs.length; i++) {
        pulled++;
        if (!appendPage(docs[i])) {
          exhausted = true;
          break;
        }
      }
      yield_ = (cardCount() - before) / pulled;
    }).catch(function () {
      // Leave the button in place so the reader can still load more by hand
      exhausted = true;
    }).then(function () {
      filling = false;
      fill();
    });
  }

  /* Replaces the numbers of a localized string in order, language agnostic */
  function replaceNumbers(text, numbers) {
    var i = 0;
    return text.replace(/\d[\d.,]*/g, function (match) {
      return i < numbers.length ? String(numbers[i++]) : match;
    });
  }

  function setText(el, value) {
    if (el && el.textContent !== value) el.textContent = value;
  }

  /* A user click asks for one more page worth of products. The component owns
     that request, so hold off until the section it re-renders has settled. */
  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('pagination-load-more a');
    if (!link || !root.contains(link)) return;
    userLink = link;
    steps = 0;
    target = cardCount() + PAGE_SIZE;
    if (lastTotal != null && target > lastTotal) target = lastTotal;
  });

  function apply() {
    // Detached means the section was re-rendered; no aria-loading means it failed
    if (userLink && (!document.contains(userLink) || !userLink.hasAttribute('aria-loading'))) {
      userLink = null;
    }

    var element = grid();
    if (!element) return;

    var key = cacheKey();
    if (key !== lastKey) {
      // Filters or sort changed: the grid starts over
      lastKey = key;
      lastTotal = null;
      target = PAGE_SIZE;
      steps = 0;
      exhausted = false;
      yield_ = null;
    }

    // Start pulling right away; the total below only refines the stopping point
    fill();

    availableTotal().then(function (total) {
      if (!root.contains(element) || cacheKey() !== key) return;
      lastTotal = total;
      if (total == null) return;

      var shown = cardCount();

      var counter = element.querySelector('.collection-grid__results-count .text-sm');
      if (counter) setText(counter, replaceNumbers(counter.textContent, [total]));

      if (target > total) target = total;

      var more = loadMore();
      if (!more) return;
      if (shown >= total) {
        more.remove();
        return;
      }
      var progressText = more.querySelector('p');
      if (progressText) setText(progressText, replaceNumbers(progressText.textContent, [shown, total]));
      var bar = more.querySelector('.progress-bar-inner');
      if (bar) {
        var width = Math.min(100, Math.round((shown / total) * 100)) + '%';
        if (bar.style.width !== width) bar.style.width = width;
      }
    });
  }

  /* Filters and the load-more button replace the section HTML through the
     Section Rendering API: re-apply after every re-render */
  var scheduled = null;
  new MutationObserver(function () {
    clearTimeout(scheduled);
    scheduled = setTimeout(apply, 100);
  }).observe(root, { childList: true, subtree: true });

  apply();
})();
